import { Button, Callout, Flex, Link, Text, TextField } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import { NavLink, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { routes } from "../../../routes";
import { AuthRoot } from "../auth-root.component";
import { authClient } from "../client/auth-client";
import { AuthErrorMessage } from "../components/auth-error-message.component";
import { strongPasswordValidation } from "../validation/auth-validation";

const resetPasswordValidationSchema = Yup.object({
  password: strongPasswordValidation,
});

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const resetPasswordMutation = useMutation({
    mutationFn: async (input: { newPassword: string; resetToken: string }) => {
      await authClient.resetPassword({
        newPassword: input.newPassword,
        token: input.resetToken,
      });
    },
  });

  return (
    <AuthRoot title="Passwort zurücksetzen">
      {!token ? (
        <AuthErrorMessage message="Der Link zum Zurücksetzen ist ungültig." />
      ) : !resetPasswordMutation.isSuccess ? (
        <Formik
          initialValues={{ password: "" }}
          validationSchema={resetPasswordValidationSchema}
          validateOnMount
          onSubmit={(values) => {
            resetPasswordMutation.mutate({
              newPassword: values.password,
              resetToken: token,
            });
          }}
        >
          {({
            values,
            errors,
            isValid,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text
                    as="label"
                    size="2"
                    weight="medium"
                    htmlFor="reset-password-new-password"
                  >
                    Neues Passwort
                  </Text>
                  <TextField.Root
                    id="reset-password-new-password"
                    required
                    type="password"
                    size="3"
                    placeholder="Neues Passwort"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {values.password.length > 0 && errors.password && (
                    <Text size="1" color="red">
                      {errors.password}
                    </Text>
                  )}
                </Flex>
                <Button
                  type="submit"
                  size="3"
                  variant="soft"
                  loading={resetPasswordMutation.isPending}
                  disabled={!isValid || resetPasswordMutation.isPending}
                >
                  Passwort zurücksetzen
                </Button>
              </Flex>
            </form>
          )}
        </Formik>
      ) : (
        <Callout.Root color="green">
          <Callout.Text>Ihr Passwort wurde aktualisiert.</Callout.Text>
        </Callout.Root>
      )}

      {resetPasswordMutation.isError && (
        <AuthErrorMessage error={resetPasswordMutation.error} />
      )}

      <Link asChild>
        <NavLink to={routes.auth.login}>Zum Login</NavLink>
      </Link>
    </AuthRoot>
  );
}
